/**
 * The admin console's one-time code, from the address it opened this tab at.
 *
 * "Maker portal ↗" in the admin console opens `<Maker portal>/#handoff=<code>`:
 * a single-use code, valid 60 seconds, that signs an AileFX operator in to
 * AileFX's desk. It travels in the fragment, which reaches no server log and no
 * Referer, and it must reach nothing else here either:
 *
 * - `captureHandoffFragment()` runs before every other module of the app
 *   (`src/handoffFirst.ts`, which `main.tsx` imports first), so before the
 *   error reporter starts: every report carries `location.href`. It takes the
 *   fragment out of the address bar with `history.replaceState`, path and
 *   query kept, and keeps the code in this module's memory only: never in
 *   localStorage, sessionStorage, a log line or a report.
 * - `takeHandoffCode()` hands it over once, to the exchange (`handoff.ts`),
 *   and forgets it.
 *
 * This module imports nothing, so importing it first runs nothing else first.
 */

/** The fragment's key in the admin console's link: `#handoff=<code>`. */
export const HANDOFF_PARAM = "handoff";

let code: string | null = null;
let captured = false;

/**
 * Reads `#handoff=<code>` and removes the whole fragment from the address bar
 * (path and query kept), whatever its value. Returns whether there was one.
 * A fragment without the key is left alone.
 */
export function captureHandoffFragment(
  loc: Pick<Location, "hash" | "pathname" | "search"> = window.location,
  hist: Pick<History, "replaceState" | "state"> = window.history,
): boolean {
  const fragment = loc.hash.replace(/^#/, "");
  if (fragment === "") return false;
  const params = new URLSearchParams(fragment);
  if (!params.has(HANDOFF_PARAM)) return false;
  hist.replaceState(hist.state, "", `${loc.pathname}${loc.search}`);
  code = params.get(HANDOFF_PARAM) ?? "";
  captured = true;
  return true;
}

/** True from the capture until the code is taken: a handoff waits to be exchanged. */
export function hasHandoffCode(): boolean {
  return captured && code !== null;
}

/** The code, once: it is forgotten as it is handed over. Null when none was captured or it was taken. */
export function takeHandoffCode(): string | null {
  const value = code;
  code = null;
  return value;
}

/** Tests only: as if the page loaded without a fragment. */
export function resetHandoffCode(): void {
  code = null;
  captured = false;
}
